import type { CoBuyer, IShopper, VehicleInsurance } from '@makemydeal/dr-platform-types';
import type { BridgeUserState } from '@makemydeal/dr-dash-types';
import type { CardOperation } from './cardOperation.types';
import type { CardSearchState } from './search.types';
import type { CustomerCardSpdState, CustomerSpdValues } from './customerSpd.types';
import type { Address } from '../components/sections/addresses/address.utils';
import type { ContractApiErroredVehicleInsuranceInfo, InsuranceProviderOption } from '@makemydeal/dr-shared-components';
import type { CUSTOMER_CARD_ANALYTICS } from '../analytics';

export const CUSTOMER_TABS = {
    BUYER: 'buyer',
    CO_BUYER: 'coBuyer',
    BUYER_DETAILS: 'buyerDetails',
    CO_BUYER_DETAILS: 'coBuyerDetails',
    INSURANCE: 'insurance'
} as const;

export type CustomerTabKey = typeof CUSTOMER_TABS.BUYER | typeof CUSTOMER_TABS.CO_BUYER;
export type CustomerSubTabKey =
    | typeof CUSTOMER_TABS.BUYER_DETAILS
    | typeof CUSTOMER_TABS.CO_BUYER_DETAILS
    | typeof CUSTOMER_TABS.INSURANCE;

export type CustomerCardSaveStatus = 'idle' | 'saving' | 'success' | 'error';

export type SwapStatus = 'idle' | 'saving' | 'success' | 'error';

export interface CustomerCardState {
    buyer?: IShopper;
    coBuyer?: CoBuyer | null;
    hasCoBuyer: boolean;
    initialTab: CustomerTabKey;
    saveStatus: CustomerCardSaveStatus;
    /** Set when apply fails; cleared on the next apply attempt or successful save. */
    saveError?: string;
    /** Raw error payload from the save-participants RSAA. Set on FAILURE or SUCCESS with ok:false; cleared on REQUEST. */
    saveParticipantsError?: unknown;
    swapStatus?: SwapStatus;
    swapError?: string;
    dropCoBuyerStatus?: SwapStatus;
    dropCoBuyerError?: string;
    /** Current vehicle insurance payload seeded from the host store. */
    vehicleInsuranceInfo?: VehicleInsurance | null;
    /** Whether the insurance info has been updated externally. */
    isVehicleInsuranceUpdated?: boolean;
    /** Fields the contract API reported as missing/invalid on the current vehicle insurance, seeded from the host store. */
    contractApiErroredVehicleInsurance?: ContractApiErroredVehicleInsuranceInfo | null;
    /**
     * DealXG id, seeded via the seedCustomerCard payload. Lives on the main slice (not SPD) because it
     * is a general deal property, not SPD-specific. SPD reads it from here.
     */
    dealId?: string;
    /** Dealer id the card uses for its BFF/analytics calls, seeded via the seedCustomerCard payload. */
    dealerId?: number;
    /** Cached insurance provider options fetched from static asset. */
    insuranceProviders?: InsuranceProviderOption[];
    /** Raw error payload from the save-insurance RSAA. Set on FAILURE or SUCCESS with ok:false; cleared on REQUEST. */
    saveInsuranceError?: unknown;
    /** The `search` step's results, shared by every operation that searches. */
    search: CardSearchState;
    /** The card operation in progress, or null for the ordinary card with no operation running. */
    operation: CardOperation | null;
    /**
     * What the `edit` step seeds the form with, in place of the seeded participant. `null` falls back to
     * the participant; an empty-but-present draft is how a new customer asks for a blank form.
     */
    draft: IShopper | CoBuyer | null;
    /** Whether the deal is sold or contracted — seeded from middleware. */
    isDealSoldOrContracted?: boolean;
    isContractGenerated?: boolean;
    /** Common org ID seeded via the seedCustomerCard payload — used for business conversion API calls. */
    commonOrgId?: string;
    /** True when the last successful save is the one that granted credit consent — drives the one-time banner. */
    creditConsentJustGranted?: boolean;
}

export interface CustomerCardSeedPayload {
    /** Omitted only for a deal with no buyer yet, where an `addCustomer` operation supplies one. */
    buyer?: IShopper;
    coBuyer?: CoBuyer | null;
    /** Indicates co-buyer participant exists in the deal. */
    hasCoBuyer: boolean;
    initialTab?: CustomerTabKey;
    vehicleInsuranceInfo?: VehicleInsurance | null;
    isVehicleInsuranceUpdated?: boolean;
    /** Fields the contract API reported as missing/invalid on the current vehicle insurance. */
    contractApiErroredVehicleInsurance?: ContractApiErroredVehicleInsuranceInfo | null;
    /** DealXG id — needed by SPD calls; stored on the slice so components read it via selector. */
    dealId?: string;
    /**
     * Dealer id the card uses for its BFF/analytics calls. Hosts seed it explicitly — the card does not
     * fall back to the host's shared `dealer.dealerId`, whose meaning differs per host (e.g. SCI holds
     * the VinSolutions id there, not the MMD id the BFF expects). Omitted by hosts that don't use SPD or
     * the insurance tab (e.g. the SCI Showroom card).
     */
    dealerId?: number;
    /**
     * Controls whether the activity middleware fires the SPD presence CHECK (both participants) on
     * seed. Defaults to `true` (pre-fetch is on) when omitted — hosts opt out by passing `false`.
     */
    prefetchSpd?: boolean;
    /** Whether the deal is sold or contracted — guards business conversion CTA. */
    isDealSoldOrContracted?: boolean;
    isContractGenerated?: boolean;
    /** Common org ID — needed for business conversion API calls. */
    commonOrgId?: string;
}

export interface CustomerCardImperativeRef {
    apply: () => Promise<void>;
    cancel: () => void;
    /** Entry points — the host picks which operation to start. Optional; not every host offers them. */
    convertToBusiness?: () => void;
    continueOperation?: () => void;
    cancelOperation?: () => void;
    /** One step back: `edit` returns to `search`; `search` exits the operation. */
    goBack?: () => void;
}

/**
 * State object pushed to the host via the onStateChange prop.
 * Add fields here as the card acquires new trackable status — the prop
 * signature and host handler stay unchanged.
 */
export interface CustomerCardHostState {
    /** True when at least one form field has been changed from its seeded value and no validation errors. */
    canApply: boolean;
    /** True once the card is mounted; may be false in future if a blocking flow is active. */
    canCancel: boolean;
    /** True while a save operation is in flight. */
    isSaving: boolean;
    /** True when the card has encountered an unrecoverable render error and the error boundary fallback is shown. */
    hasRenderError?: boolean;
    /** True when a search result is selected, whichever operation is searching. */
    canContinue?: boolean;
    /**
     * The card operation in progress; `null` or absent means none. Hosts branch on `step` to pick their
     * chrome, so a new operation kind needs no host change.
     */
    operation?: CardOperation | null;
    /** The running operation's title, for hosts that render it in their own chrome. */
    operationTitle?: string;
    /** True when the running operation has a step to return to; hosts draw their back affordance from it. */
    canGoBack?: boolean;
    /** The sub-tab currently shown. */
    activeSubTab?: CustomerSubTabKey;
}

/**
 * Capabilities passed from the host to opt into optional behaviours.
 * Hosts that don't support a capability simply omit it (defaults to off).
 */
export interface CustomerCardCapabilities {
    /** Enable contract-flow validation rules (e.g. MV contracting context). */
    contractValidation?: boolean;
    /** Render all fields in read-only / locked mode. */
    readOnly?: boolean;
    /**
     * Signal that the deal is in Optional Desking mode.
     * When true, required fields show "(required)" in their label instead of the asterisk,
     * mirroring the legacy CustomerCard behaviour.
     */
    optionalDesking?: boolean;
    /**
     * Enable the Sensitive Personal Data (SPD) section. When off, the SPD section is not rendered
     * at all. Requires a Common Consumer ID at runtime; silently skipped for participants without one.
     */
    enableSpd?: boolean;
    /** Enable the "Convert to Business" flow. */
    enableBusinessConversion?: boolean;
    /** Host override for the credit-app fields; when omitted, falls back to the `enableCreditAppFieldsCoverage` toggle. */
    enableCreditAppFields?: boolean;
    /**
     * Host override to force-hide the SPD "Show Data" CTA and bank-account reveal; when explicitly `false`, the
     * reveal is never offered regardless of canDesk/isAuthorized. Omitted = existing toggle/canDesk behavior.
     */
    enableSpdReveal?: boolean;
    /** Host override for the credit-consent section (F311457); when omitted, falls back to the `isCreditAppConsentEnabled` toggle. */
    enableCreditConsent?: boolean;
    /** Hide the "SSN sent to Dealertrack F&I" line in the credit-consent banner, leaving only the title. */
    enableCreditConsentSentMessageSuppression?: boolean;
    /** Host override to hide the Change Buyer / Change Co-Buyer CTAs when explicitly `false`. Omitted = toggle behavior. */
    enableChangeBuyerCoBuyer?: boolean;
    /** Assert the host never opens the card without a buyer or an operation to supply one. */
    requireBuyer?: boolean;
    warnOnBuyerEmailChange?: boolean;
}

/**
 * Controls which fields are visible inside section sub-components.
 * 'full' (default) — all fields. 'summary' — primary fields only (firstName, lastName, phone, email).
 */
export type CustomerFormViewMode = 'full' | 'summary';

/**
 * Phone field keys used by PhoneNumberFields and handlePhoneInputChange.
 */
export type PhoneFieldKey = 'phone' | 'cellPhone' | 'homePhone' | 'workPhone' | 'business.bankContactPhoneNumber';

/**
 * Payload passed to the onSave callback after a successful save.
 * Contains the enriched form values for both participants so the consumer
 * (e.g. EditCustomerSlideout) can update offer-redux with the latest data.
 */
export interface CustomerCardSaveData {
    buyer?: CustomerFormValues;
    coBuyer?: CustomerFormValues | null;
    replaced?: { buyer: boolean; coBuyer: boolean };
    insurance?: VehicleInsurance | null;
    spdValues?: { [commonConsumerId: string]: CustomerSpdValues };
    /** True only when all API calls succeeded. Partial success sets this to false. */
    allSaved: boolean;
}

/**
 * Flat form values maintained by the validation engine inside CustomerForm.
 * Extend as fields are added in future stories.
 */
export interface CustomerFormValues {
    /** Seeded from IShopper/CoBuyer.commonConsumerId — enriched by useCustomerCardActions before passing to apply. */
    commonConsumerId?: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    title?: string;
    suffix?: string;
    maritalStatus?: string;
    /** Credit-consent flag (F311457) — maps to the DealXG `ParticipantInfo` field of the same name. */
    creditPullConsentGiven?: boolean;
    participantRole?: string;
    relationshipToBuyer?: string;
    phone?: string;
    cellPhone?: string;
    homePhone?: string;
    workPhone?: string;
    email?: string;
    emailAlternative?: string;
    streetAddress?: string;
    streetAddress2?: string;
    city?: string;
    state?: string;
    county?: string;
    zip?: string;
    billingAddress?: Address;
    birthDate?: IShopper['birthDate'];
    /** Populated for business customers (e.g. 'Business' or 'Individual'). */
    customerType?: string;
    /** Business-specific fields, present when customerType === 'Business'. */
    business?: {
        legalName?: string;
        businessEntityId?: string;
        taxIdNumber?: string;
        businessType?: string;
        businessEstablishedDate?: string;
        numberOfEmployees?: number;
        stateOfIncorporation?: string;
        financialStatementDescription?: string;
        bankName?: string;
        bankContactName?: string;
        bankContactPhoneNumber?: string;
        /** Folded in at apply time from CustomerFormRef.getChangedBankAccountNumber() — see useCustomerCardActions. */
        bankAccountNumber?: string;
    };
    /** SPD field — bank account number, stored top-level like SSN/DOB/DL. */
    bankAccountNumber?: string | null;
    /** Housing & Employment: Current address information */
    housingInfo?: {
        housingStatus?: string;
        mortgageOrRentAmount?: number;
    };
    yearsAtCurrentAddress?: number;
    monthsAtCurrentAddress?: number;
    /** Housing & Employment: Previous address information */
    previousAddress?: Address;
    yearsAtPreviousAddress?: number;
    monthsAtPreviousAddress?: number;
    /** Housing & Employment: Current employment information */
    currentEmployment?: {
        status?: string;
        employerName?: string;
        occupation?: string;
        workPhone?: string;
        totalYearsEmployed?: number;
        totalMonthsEmployed?: number;
        address?: Omit<Address, 'streetAddress2' | 'county'>;
    };
    /** Housing & Employment: Previous employment information */
    previousEmployment?: {
        status?: string;
        employerName?: string;
        occupation?: string;
        workPhone?: string;
        totalYearsEmployed?: number;
        totalMonthsEmployed?: number;
    };
    /** Housing & Employment: Financial information */
    financialInfo?: {
        otherIncomeSource?: string;
        otherIncome?: number;
        income?: number;
        incomeFrequency?: string;
    };
    [key: string]: unknown;
}

/**
 * Shape of the Redux store slices owned by this activity.
 * `customerCard` holds core customer data (incl. dealId); `activitiesCustomerSpd` is the separate SPD
 * slice — namespaced to avoid colliding with dr-dash's own legacy `customerSpd` slice in the MV host.
 */
export type CustomerCardStoreState = {
    customerCard?: CustomerCardState;
    activitiesCustomerSpd?: CustomerCardSpdState;
    bridgeUser?: Partial<BridgeUserState>;
};

/**
 * Ref surface exposed by CustomerForm to its parent orchestrators
 * (CustomerCard and UnifiedCustomerCard). Never performs a save itself.
 */
export interface CustomerFormRef {
    /** Run validateAll and return true if the form has no active errors. */
    validate: () => Promise<boolean>;
    /** Return a snapshot of the current engine values. */
    getValues: () => CustomerFormValues;
    /** True when current values differ from the seeded initial values. */
    isDirty: () => boolean;

    // True when any SPD field (SSN, DOB, DL) differs from the SPD baseline.
    isSpdDirty: () => boolean;

    // Returns the current personal SPD fields (SSN, DOB, DL) — a plain read, no ccid, no dirty check.
    getSpdValues: () => CustomerSpdValues;
    /**
     * Clears engine errors and resets the dirty-detection baseline. Call after a successful save.
     */
    reset: () => void;
}

export type RawInsuranceProvider = {
    Name: string;
    Address?: string;
    City?: string;
    State?: string;
    ZIP?: string;
    Phone?: string;
    Abbreviation?: string;
};

export type CustomerCardAnalyticsEvent = (typeof CUSTOMER_CARD_ANALYTICS)[keyof typeof CUSTOMER_CARD_ANALYTICS];

export type OnAnalyticsEvent = (eventName: CustomerCardAnalyticsEvent, data?: Record<string, unknown>) => void;
